/**
 * Fuzzy test for string matching pattern.
 * @param {string} str - String to test.
 * @param {string} pattern - Pattern to match.
 * @returns {boolean} - True if string matches pattern.
 */
export function fuzzyTest(str: string, pattern: string) {
  let patternIdx = 0
  let stringIdx = 0
  while (patternIdx < pattern.length && stringIdx < str.length) {
    if (pattern[patternIdx] === str[stringIdx]) {
      patternIdx++
      stringIdx++
    } else {
      stringIdx++
    }
  }
  return patternIdx === pattern.length
}

/**
 * Fuzzy match string with pattern and return matched parts.
 * @param {string} str - String to match.
 * @param {string} pattern - Pattern to match.
 * @returns {Array} - [ longestMatchedSequenceLength, unmatchedPart, matchedPart, unmatchedPart, matchedPart, ... ]
 */
export function fuzzyMatch(str: string, pattern: string) {
  const patternLength = pattern.length
  const strLength = str.length

  if (0 === patternLength || patternLength > strLength) {
    return [0, str]
  } else if (1 === patternLength) {
    const matchFrom = str.indexOf(pattern)
    return -1 === matchFrom ? [0, str] : [1, str.slice(0, matchFrom), pattern, str.slice(matchFrom + 1)]
  } else if (pattern === str) {
    return [patternLength, '', pattern]
  }

  const result: [number, ...string[]] = [0]
  let currentPart = ''
  let currentPartMatched = false

  let patternIdx = 0
  let stringIdx = 0
  while (patternIdx < patternLength && stringIdx < strLength) {
    if (pattern[patternIdx] === str[stringIdx]) {
      if (!currentPartMatched) {
        result.push(currentPart)
        currentPart = ''
        currentPartMatched = true
      }
      currentPart += str[stringIdx]
      patternIdx++
      stringIdx++
    } else {
      if (currentPartMatched) {
        if (result[0] < currentPart.length) {
          result[0] = currentPart.length
        }
        result.push(currentPart)
        currentPart = ''
        currentPartMatched = false
      }
      currentPart += str[stringIdx]
      stringIdx++
    }
  }

  if (patternIdx === patternLength) {
    if (currentPart.length) {
      if (result[0] < currentPart.length) {
        result[0] = currentPart.length
      }
      result.push(currentPart)
    }
    if (stringIdx < strLength) {
      result.push(str.slice(stringIdx))
    }
    return result
  } else {
    return [0, str]
  }
}

/**
 * Calculates the Levenshtein distance between two strings, returning counts for each operation.
 *
 * The Levenshtein distance is a measure of the difference between two sequences.
 * This function returns the count of each type of single-character edit (substitutions,
 * insertions, and deletions) required to change one word into the other.
 *
 * @param {string} s1 - The first string to compare.
 * @param {string} s2 - The second string to compare.
 * @returns {[number, number, number]} An array containing the counts of [substitutions, insertions, deletions].
 *                                     If maxDistance is provided and the actual distance exceeds it,
 *                                     the function may return counts that sum to a value greater than maxDistance.
 *
 * @example
 * levenshteinDistance("kitten", "sitting"); // returns [1, 1, 1]
 */
export function levenshteinDistance(s1: string, s2: string): [number, number, number] {
  const m = s1.length
  const n = s2.length
  const dp: number[][] = Array<number[]>(m + 1)
    .fill(null)
    .map(() => Array<number>(n + 1).fill(0))

  // Initialize the first row and column
  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j

  // Fill the dp table
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        s1[i - 1] === s2[j - 1]
          ? dp[i - 1][j - 1]
          : Math.min(
              dp[i - 1][j] + 1, // deletion
              dp[i][j - 1] + 1, // insertion
              dp[i - 1][j - 1] + 1 // substitution
            )
    }
  }

  // Backtrack to count operations
  let substitutions = 0
  let insertions = 0
  let deletions = 0
  let i = m
  let j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && s1[i - 1] === s2[j - 1]) {
      i--
      j--
    } else if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
      substitutions++
      i--
      j--
    } else if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      deletions++
      i--
    } else {
      insertions++
      j--
    }
  }

  return [substitutions, insertions, deletions]
}
